import { linkifyText } from "@/src/utils/linkify.utils";
import { Linking, Text, TextProps } from "react-native";

/**
 * The link segments on their own, with no wrapping element.
 *
 * Use this where the surrounding `<Text>` already has other children — a comment
 * prefixes the parent author's handle, so its wrapper cannot be replaced
 * outright. Nesting `<Text>` inside `<Text>` is how RN composes inline runs, so
 * these stay on the same line and inside the parent's clamping.
 *
 * `Linking.openURL` needs no platform branch: on web react-native-web opens with
 * `target="_blank"` and `noopener` by default, and on native it hands off to the
 * system browser.
 */
export const LinkifiedSegments = ({ text }: Readonly<{ text?: string | null }>) => (
  <>
    {linkifyText(text ?? "").map((segment, index) =>
      segment.url ? (
        <Text
          // Segments are positional, so the index is the identity here.
          key={`link-${index}`}
          className="text-primary underline"
          onPress={() => {
            Linking.openURL(segment.url as string).catch(() => {
              // A blocked pop-up or unsupported scheme is not worth a crash.
            });
          }}
          accessibilityRole="link"
        >
          {segment.text}
        </Text>
      ) : (
        segment.text
      ),
    )}
  </>
);

type LinkifiedTextProps = Readonly<
  Omit<TextProps, "children"> & {
    /** Body copy that may contain URLs. */
    children?: string | null;
  }
>;

/**
 * Body text with any URLs rendered as tappable links.
 *
 * Drop-in for a plain `<Text>`: every prop is forwarded to the outer element, so
 * `numberOfLines`, `ellipsizeMode` and `onLayout` keep working.
 */
const LinkifiedText = ({ children, ...textProps }: LinkifiedTextProps) => (
  <Text {...textProps}>
    <LinkifiedSegments text={children} />
  </Text>
);

export default LinkifiedText;
